// Paso 3 (PE-A3b): guardado en emergency_assessments y recarga.
//  - inputs: las entradas del responsable (lo único que es dato fuente).
//  - result: el resumen calculado y su "firma" (estado y valor de cada traza), como instantánea.
//  - traces: las trazas completas (método, norma, artículo o tabla, umbral, valor, unidad, estado y fuente).
// Al abrir de nuevo se recalcula desde las entradas; si la firma guardada no coincide con la recalculada (cambió
// el motor o un dato de los pasos 1 y 2), se avisa "el resultado cambió desde la última vez que se guardó". No se
// corrige solo: el responsable vuelve a guardar.

import { borrarEvaluacionesMeseri, FilaEvaluacion, guardarEvaluacion } from './pe-datos';
import { entradasVacias, EntradasPaso3, Evaluacion, firmaResultado } from './pe-paso3';

export function inputsDe(ev: Evaluacion, e: EntradasPaso3): Record<string, unknown> {
  switch (ev.metodo) {
    case 'meseri': {
      const unidad = ev.clave.slice('meseri:'.length);
      return { unidad, entrada: e.meseri[unidad] ?? { factores: {}, medios: {} } };
    }
    case 'aforo_egreso': return { entrada: e.aforo };
    case 'rset': return { entrada: e.evacuacion };
    case 'punto_encuentro': return { entrada: e.punto };
    case 'verificacion': return { entrada: e.verificacion };
    case 'amenaza_vulnerabilidad': return { entrada: e.amenazas };
    default: return {};
  }
}

// Entradas desde las filas guardadas. Las filas de PE-A3a (sin "entrada") se ignoran. Si se pasan las
// cabezas actuales de MESERI (sector que encabeza cada sector suelto o grupo), las filas meseri de otros
// sectores (grupos anteriores) se ignoran: solo cuentan las de los encabezados actuales.
export function entradasDesdeFilas(filas: FilaEvaluacion[], cabezas?: Set<string>): EntradasPaso3 {
  const e = entradasVacias();
  for (const f of filas) {
    const ent = (f.inputs as any)?.entrada;
    if (ent === undefined) continue;
    if (f.method === 'meseri' && cabezas && !(f.sector_id && cabezas.has(f.sector_id))) continue;
    if (f.method === 'meseri' && typeof (f.inputs as any).unidad === 'string') e.meseri[(f.inputs as any).unidad] = ent;
    else if (f.method === 'aforo_egreso') e.aforo = { salidas: [], ...ent };
    else if (f.method === 'rset') e.evacuacion = ent;
    else if (f.method === 'punto_encuentro') e.punto = { condiciones: {}, peligros: {}, ...ent };
    else if (f.method === 'verificacion') e.verificacion = ent;
    else if (f.method === 'amenaza_vulnerabilidad' && Array.isArray(ent)) e.amenazas = ent;
  }
  return e;
}

export function filaDe(ev: Evaluacion, filas: FilaEvaluacion[]): FilaEvaluacion | undefined {
  return filas.find(f => f.method === ev.metodo && (f.sector_id ?? null) === ev.sectorId
    && (ev.metodo !== 'meseri' || (f.inputs as any)?.unidad === ev.clave.slice('meseri:'.length)));
}

// Claves de las evaluaciones cuyo resultado guardado ya no coincide con el recalculado.
export function resultadosCambiados(evaluaciones: Evaluacion[], filas: FilaEvaluacion[]): string[] {
  return evaluaciones.filter(ev => {
    const f = filaDe(ev, filas);
    const firma = (f?.result as any)?.firma;
    return typeof firma === 'string' && firma !== firmaResultado(ev.trazas);
  }).map(ev => ev.clave);
}

// ── MESERI de grupos anteriores ──────────────────────────────────────────────
// El MESERI de un grupo sin separación cortafuego se guarda con el sector_id del primer sector del grupo. Si
// el grupo cambia, esa fila queda suelta: su sector ya no encabeza ningún grupo ni sector suelto actual.

export function cabezasMeseri(evaluaciones: Evaluacion[]): Set<string> {
  return new Set(evaluaciones.filter(ev => ev.metodo === 'meseri' && ev.sectorId).map(ev => ev.sectorId as string));
}

export function meseriHuerfanas(filas: FilaEvaluacion[], evaluaciones: Evaluacion[]): FilaEvaluacion[] {
  const cabezas = cabezasMeseri(evaluaciones);
  return filas.filter(f => f.method === 'meseri' && !(f.sector_id && cabezas.has(f.sector_id)));
}

// Se llama solo después de un guardado correcto del paso 3. Borrar exige el rol admin (RLS de M33): con
// manager no se borra nada y las filas quedan "pendientes" (se ignoran al leer y el paso 4 avisa).
export async function limpiarMeseriHuerfanas(
  planId: string, filas: FilaEvaluacion[], evaluaciones: Evaluacion[], puedeBorrar: boolean,
): Promise<{ borradas: number; pendientes: number }> {
  const huerfanas = meseriHuerfanas(filas.filter(f => f.plan_id === planId), evaluaciones);
  if (huerfanas.length === 0) return { borradas: 0, pendientes: 0 };
  if (!puedeBorrar) return { borradas: 0, pendientes: huerfanas.length };
  const borradas = await borrarEvaluacionesMeseri(planId, huerfanas.map(f => f.id));
  return { borradas, pendientes: huerfanas.length - borradas };
}

export async function guardarPaso3(
  companyId: string, planId: string, evaluaciones: Evaluacion[], e: EntradasPaso3, usuarioId: string | null,
): Promise<FilaEvaluacion[]> {
  const out: FilaEvaluacion[] = [];
  for (const ev of evaluaciones) {
    out.push(await guardarEvaluacion({
      companyId, planId, sectorId: ev.sectorId, method: ev.metodo, usuarioId,
      inputs: inputsDe(ev, e),
      result: { ...ev.resumen, titulo: ev.titulo, firma: firmaResultado(ev.trazas) },
      traces: ev.trazas,
    }));
  }
  return out;
}
